import * as React from "react"
import { useEffect, useState } from "react"
import { Link as RouterLink } from "react-router-dom"
import {
	Alert, Box, Chip, CircularProgress, Divider, Drawer, IconButton,
	Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
	Tooltip, Typography,
} from "@mui/material"
import HistoryIcon from '@mui/icons-material/History'
import CloseIcon from '@mui/icons-material/Close'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'

import { BACKEND_APIS, VISIBILITY_TRACKER_ROUTES } from "@constants"
import { useAppToast } from "@ui-seeker/app"

import { vtApi as api } from "./api"


// MUI Chip colour per pipeline stage. Local copy so the drawer is
// self-contained and usable from any VT page (Pipeline, Admin, …).
const STAGE_COLOR = {
	pending: 'default', in_progress: 'warning', extracted: 'info',
	scored: 'primary', matched: 'primary', ai_reviewed: 'secondary',
	reviewed: 'success', failed: 'error', expired: 'default',
}


// A section's execution history across all dates. Opened from Pipeline
// (row drill-down) and from Admin (section row). Pass `idSection` to
// open; null/0 keeps it closed.
export default function SectionHistoryDrawer({ idSection, onClose }) {
	const { showToast } = useAppToast()
	const [loading, setLoading] = useState(false)
	const [data, setData] = useState(null)

	useEffect(() => {
		if (!idSection) { setData(null); return }
		let cancelled = false
		setLoading(true)
		api.get(`${BACKEND_APIS.VT}/admin/sections/${idSection}/history`).then(r => {
			if (cancelled) return
			setLoading(false)
			if (!r.ok) { showToast.error('Failed to load history: ' + r.error); return }
			setData(r.data)
		})
		return () => { cancelled = true }
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [idSection])

	const section = data?.section
	return <Drawer anchor="right" open={Boolean(idSection)} onClose={onClose}
		PaperProps={{ sx: { width: { xs: '100%', sm: 560 } } }}>
		<Box sx={{ p: 2 }}>
			<Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
				<HistoryIcon fontSize="small" />
				<Box sx={{ flex: 1, minWidth: 0 }}>
					<Typography variant="overline" sx={{ display: 'block', lineHeight: 1, color: 'text.secondary' }}>
						Section history
					</Typography>
					<Typography variant="h6" sx={{ lineHeight: 1.2 }}>
						{data?.retailer?.name ? `${data.retailer.name} · ` : ''}{section?.name || '…'}
					</Typography>
				</Box>
				<IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
			</Stack>
			<Divider sx={{ mb: 1 }} />
			{loading ? (
				<Stack alignItems="center" sx={{ py: 6 }}><CircularProgress size={22} /></Stack>
			) : !data || data.items.length === 0 ? (
				<Alert severity="info">No runs recorded for this section yet.</Alert>
			) : (
				<TableContainer>
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>Date</TableCell>
								<TableCell>Stage</TableCell>
								<TableCell align="right">QA</TableCell>
								<TableCell align="right">Export</TableCell>
								<TableCell align="right" />
							</TableRow>
						</TableHead>
						<TableBody>
							{data.items.map(it => {
								const reviewParams = new URLSearchParams({
									id_retailer: String(data.retailer?.id_retailer || ''),
									id_section: String(section.id_section),
									date: it.task_date,
								})
								if (it.asset_path) reviewParams.set('asset_path', it.asset_path)
								const href = `${VISIBILITY_TRACKER_ROUTES.REVIEW_PATH}?${reviewParams.toString()}`
								const okItems = (it.total_items_count || 0) - (it.needs_qa_count || 0)
								return <TableRow key={it.id_task} hover>
									<TableCell>
										<Typography variant="body2">{it.task_date}</Typography>
										{it.asset_path && (
											<Typography variant="caption" sx={{ color: 'text.secondary', wordBreak: 'break-all' }}>{it.asset_path}</Typography>
										)}
									</TableCell>
									<TableCell>
										<Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
											<Chip size="small" label={it.pipeline_stage} color={STAGE_COLOR[it.pipeline_stage] || 'default'} />
											{it.failed_stage && (
												<Tooltip title={it.last_error || '(no error message)'}>
													<Chip size="small" variant="outlined" color="error" label={`@ ${it.failed_stage}`} />
												</Tooltip>
											)}
										</Stack>
									</TableCell>
									<TableCell align="right">
										{it.total_items_count > 0
											? <Typography variant="caption">{okItems}/{it.total_items_count}</Typography>
											: <Typography variant="caption" color="text.disabled">—</Typography>}
									</TableCell>
									<TableCell align="right">
										{it.exported_at
											? <Chip size="small" color="success" variant="outlined" label="✓" />
											: <Typography variant="caption" color="text.disabled">—</Typography>}
									</TableCell>
									<TableCell align="right">
										<Tooltip title="Open in Banners Review">
											<IconButton size="small" component={RouterLink} to={href}>
												<OpenInNewIcon fontSize="small" />
											</IconButton>
										</Tooltip>
									</TableCell>
								</TableRow>
							})}
						</TableBody>
					</Table>
				</TableContainer>
			)}
		</Box>
	</Drawer>
}
